import { FC } from "react";

import { ValueOfObjectFields } from "shared/lib/interfaces/utils";
import { BACKGROUND_SHAPES } from "shared/ui/background-shape/constants";
import { clsxMerge } from "shared/lib/helpers";
import { useDynamicImage } from "shared/lib/hooks/use-dynamic-image";

interface BackgroundShapePropsI {
  className?: string;
  imageClassName?: string;
  loadedImageClassName?: string;
  shape: ValueOfObjectFields<typeof BACKGROUND_SHAPES>;
  isLazyLoaded?: boolean;
}

/**
 * The component used to render the abstract background shapes with gradients.
 * @param className - container classname. NOTE, 'absolute' position is applied BY DEFAULT.
 * @param imageClassName - classname for the image element.
 * @param loadedImageClassName - classname for the image element when it's loaded and shown.
 * @param shape - The shape to render represented as URL. Use BACKGROUND_SHAPES map to get the needed figure.
 * @param isLazyLoaded - whether the image should be lazy loaded or not. Default is true.
 */
export const BackgroundShape: FC<BackgroundShapePropsI> = ({
  className,
  imageClassName,
  loadedImageClassName,
  shape,
  isLazyLoaded = true,
}) => {
  const { isImageLoaded, imageClassName: animatedImageClassName } =
    useDynamicImage(shape);

  return (
    <div
      className={clsxMerge(
        "pointer-events-none absolute -z-[2] select-none",
        className
      )}
    >
      <img
        loading={isLazyLoaded ? "lazy" : "eager"}
        alt="Abstract background shape"
        src={shape}
        className={clsxMerge(
          animatedImageClassName,
          imageClassName,
          isImageLoaded && loadedImageClassName
        )}
      />
    </div>
  );
};
